<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import Button from 'primevue/button';
import { CheckCircleIcon } from '@heroicons/vue/24/solid';
import { getAccountPlaceSuggestion } from '@gpx-spa/queries';
import { formatAvgDwellHours, formatLastActive } from './formatters';
import { normalizePolygon } from './geometry';
import { usePlaceSuggestionsMapStore } from '@/stores/usePlaceSuggestionsMapStore';
import { geofenceCreationDefaultParams } from '@/utils/account-defaults';
import * as events from '@/events';

const props = defineProps<{
  suggestionId: string;
  rank: number;
}>();

const emit = defineEmits<{ (e: 'dismiss', id: string): void }>();

const store = useStore();
const router = useRouter();
const toast = useToast();
const mapStore = usePlaceSuggestionsMapStore();

const accountId = computed<number | null>(
  () => store.getters['auth/accountId'] ?? null,
);

const suggestionIdRef = computed(() => props.suggestionId);

const { suggestion, isLoading, isError } = getAccountPlaceSuggestion(
  accountId,
  suggestionIdRef,
);

const scorePct = computed(() => {
  const s = suggestion.value;
  return s ? Math.round(s.score.composite_pct) : null;
});

const scoreTooltip = {
  value:
    'Certainty score (0–100): how confident we are that this suggestion is a real, recurring stop. It rises with more frequent and recent visits, and with tighter clustering of those visits.',
  showDelay: 150,
  hideDelay: 100,
};

const displayName = computed(
  () => suggestion.value?.name?.trim() || 'Suggested location',
);

const address = computed(() => suggestion.value?.address?.trim() || null);

const avgDwell = computed(() =>
  suggestion.value
    ? formatAvgDwellHours(suggestion.value.stats.avg_visit_seconds)
    : '',
);

const entries = computed(() =>
  suggestion.value ? suggestion.value.stats.visit_count.toLocaleString() : '',
);

const lastActive = computed(() =>
  suggestion.value ? formatLastActive(suggestion.value.stats.last_seen_t) : '',
);

const geometry = computed(() =>
  suggestion.value?.polygon ? normalizePolygon(suggestion.value.polygon) : null,
);

const isFocused = computed(() => mapStore.focusedId === props.suggestionId);

// Identifiers and scores only — no address or place name, which are customer
// location data and have no place in a product-analytics payload.
function trackingPayload(): Record<string, unknown> {
  return {
    suggestion_id: props.suggestionId,
    rank: props.rank,
    score_pct: scorePct.value,
  };
}

// Mirror this card's polygon into the map registry so DeviceMap can draw its
// numbered pin. Re-runs when the polygon loads or the rank shifts after a
// sibling card is dismissed.
watch(
  [geometry, () => props.rank],
  ([geom, rank]) => {
    if (geom) {
      mapStore.register({ id: props.suggestionId, rank, geometry: geom });
    } else {
      mapStore.unregister(props.suggestionId);
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  mapStore.unregister(props.suggestionId);
});

const creating = ref(false);
const created = ref(false);

// Clicking the card body recenters the map on this suggestion and highlights
// its pin. Action buttons stop propagation so they don't also recenter.
function onCardSelect() {
  mapStore.focus(props.suggestionId);
}

// Primary action: create the geofence directly from Scout's polygon with an
// auto-generated name. The user stays in the panel; the card flips to its
// completed state. No navigation, no place-suggestion query invalidation —
// keeping the card mounted so the review queue stays coherent.
function onCreate() {
  if (creating.value || created.value) return;

  const geom = geometry.value;
  const acctId = accountId.value;
  if (!geom || acctId == null) return;

  creating.value = true;
  const params = {
    name: displayName.value,
    address: address.value ?? '',
    ...geofenceCreationDefaultParams(store.getters['auth/accountSettings']),
    is_irregular: 1,
    geo_json: JSON.stringify(geom),
    suggestion_id: props.suggestionId,
  };

  store
    .dispatch('geofence/store', { accountId: acctId, params })
    .then(() => {
      created.value = true;
      events.trigger(
        events.names.PLACE_SUGGESTION_GEOFENCE_CREATED,
        trackingPayload(),
      );
      toast.success('Geofence created.');
    })
    .catch(() => {
      toast.error('Could not create the geofence. Please try again.');
    })
    .finally(() => {
      creating.value = false;
    });
}

// Secondary action: open the full geofence creation page with the candidate
// polygon pre-loaded, for users who want to tune name / alerts / boundary.
function onEdit() {
  if (!geometry.value) return;

  // Intent only — the geofence is created on the full form, and that
  // acceptance is recorded server-side against the suggestion id.
  events.trigger(
    events.names.PLACE_SUGGESTION_EDITOR_OPENED,
    trackingPayload(),
  );

  router.push({
    name: 'GeofenceCreateView',
    query: { suggestion_id: props.suggestionId },
  });
}

function onDismiss() {
  // Both hosts dismiss optimistically and roll back on error, so this counts
  // intent. The suggestion rows remain the count of record.
  events.trigger(events.names.PLACE_SUGGESTION_DISMISSED, trackingPayload());
  emit('dismiss', props.suggestionId);
}
</script>

<template>
  <div
    v-if="!isLoading && !isError && suggestion"
    class="cursor-pointer rounded-md border bg-white p-3 transition-shadow"
    :class="
      isFocused
        ? 'border-gpx_terracotta-300 ring-2 ring-gpx_terracotta-300'
        : 'border-gpx_black-100/10'
    "
    data-testid="suggestion-card"
    @click="onCardSelect"
  >
    <div class="flex items-start gap-3">
      <div
        class="flex size-7 shrink-0 items-center justify-center rounded-full bg-gpx_terracotta-300 text-gpx_sm font-semibold text-white"
        data-testid="suggestion-card-rank"
      >
        {{ props.rank }}
      </div>
      <div class="min-w-0 flex-1">
        <p class="truncate text-gpx_base font-semibold text-gpx_black-50">
          {{ displayName }}
        </p>
        <p v-if="address" class="truncate text-gpx_xs text-gpx_black-500">
          {{ address }}
        </p>
      </div>
      <span
        v-if="scorePct !== null"
        v-tooltip.bottom="scoreTooltip"
        class="shrink-0 rounded-md bg-gpx_terracotta-900 px-2 py-0.5 text-gpx_xs font-semibold text-gpx_terracotta-100"
        data-testid="suggestion-card-score"
      >
        {{ scorePct }}
      </span>
    </div>
    <p v-if="suggestion.rationale" class="mt-2 text-gpx_sm text-gpx_black-50">
      {{ suggestion.rationale }}
    </p>
    <div
      class="mt-3 grid grid-cols-4 gap-2 border-t border-gpx_black-100/10 pt-3"
    >
      <div>
        <p class="text-gpx_sm font-semibold text-gpx_black-50">
          {{ suggestion.stats.device_count }}
        </p>
        <p class="text-gpx_xs text-gpx_black-500">Devices</p>
      </div>
      <div>
        <p class="text-gpx_sm font-semibold text-gpx_black-50">
          {{ avgDwell }}
        </p>
        <p class="text-gpx_xs text-gpx_black-500">Avg dwell</p>
      </div>
      <div>
        <p class="text-gpx_sm font-semibold text-gpx_black-50">{{ entries }}</p>
        <p class="text-gpx_xs text-gpx_black-500">Entries</p>
      </div>
      <div>
        <p class="text-gpx_sm font-semibold text-gpx_black-50">
          {{ lastActive }}
        </p>
        <p class="text-gpx_xs text-gpx_black-500">Last active</p>
      </div>
    </div>
    <div
      v-if="created"
      class="mt-3 flex items-center gap-1.5 text-gpx_sm font-medium text-gpx_green-100"
      data-testid="suggestion-card-completed"
    >
      <CheckCircleIcon class="size-4 shrink-0" />
      <span>Geofence created</span>
    </div>
    <div v-else class="mt-3 flex gap-2" @click.stop>
      <Button
        label="Create"
        size="small"
        class="flex-1"
        :loading="creating"
        data-testid="suggestion-card-create"
        @click="onCreate"
      />
      <Button
        label="Edit"
        severity="secondary"
        outlined
        size="small"
        data-testid="suggestion-card-edit"
        @click="onEdit"
      />
      <Button
        label="Dismiss"
        severity="secondary"
        outlined
        size="small"
        @click="onDismiss"
      />
    </div>
  </div>
</template>
